import { isValidCNPJ } from '@utils/isValidCNPJ';
import { isValidCPF } from '@utils/isValidCPF';
import { z } from 'zod';

const UFs = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export const createContratoSchema = (
  limiteCredito: number,
  termoAceiteObrigatorio: boolean = true,
) =>
  z.object({
    numeroEdital: z
      .string({
        required_error: 'Número do edital é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Número do edital é obrigatório'),
    valorIS: z
      .number({
        required_error: 'Valor do IS é obrigatório',
      })
      .min(1, 'Valor inválido')
      .max(
        limiteCredito,
        `Valor não pode ser maior que R$ ${limiteCredito.toLocaleString('pt-BR')}`,
      ),
    base64Edital: z
      .string({
        required_error: 'É obrigatório anexar o edital',
        invalid_type_error: 'Arquivo inválido, envie PDF, DOC ou DOCX',
      })
      .nonempty('Você deve anexar o edital (PDF, DOC ou DOCX)'),
    nomeSegurado: z
      .string({
        required_error: 'Nome/Razão Social é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Nome/Razão Social é obrigatório'),
    cpfCnpjSegurado: z
      .string({
        required_error: 'CPF/CNPJ é obrigatório',
      })
      .min(11, 'CPF/CNPJ inválido')
      .max(18, 'CPF/CNPJ inválido')
      .refine((val) => isValidCPF(val) || isValidCNPJ(val), {
        message: 'CPF ou CNPJ inválido',
      }),
    cep: z
      .string({
        required_error: 'CEP é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'CEP é obrigatório'),
    uf: z.enum(UFs, {
      required_error: 'Estado (UF) é obrigatório',
      invalid_type_error: 'Estado (UF) inválido',
    }),
    logradouro: z
      .string({
        required_error: 'Logradouro é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Logradouro é obrigatório'),
    numero: z
      .string({
        required_error: 'Número é obrigatório',
      })
      .regex(/^\d+$/, 'O número deve conter apenas dígitos')
      .refine((v) => v.trim().length > 0, 'Número é obrigatório'),
    complemento: z
      .string({
        required_error: 'Complemento é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Complemento é obrigatório'),
    municipio: z
      .string({
        required_error: 'Município é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Município é obrigatório'),
    bairro: z
      .string({
        required_error: 'Bairro é obrigatório',
      })
      .refine((v) => v.trim().length > 0, 'Bairro é obrigatório'),
    dtInicioVigencia: z
      .string({
        required_error: 'Data de início da vigência é obrigatória',
      })
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato da data deve ser 'yyyy-mm-dd'"),
    dtFimVigencia: z
      .string({
        required_error: 'Data de fim da vigência é obrigatória',
      })
      .refine((val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val), {
        message: "Formato da data deve ser 'yyyy-mm-dd'",
      }),
    ...(termoAceiteObrigatorio
      ? {
        termoAceite: z.literal(true, {
          required_error: 'Você deve aceitar os termos e condições',
        }),
      }
      : {}),
  });

export type ContratoFormData = z.infer<ReturnType<typeof createContratoSchema>>;
